import React, { useState, useMemo, useCallback } from 'react';
import ExplanationRenderer from './ExplanationRenderer';
import MathDisplay from './MathDisplay';
import { InlineMath } from 'react-katex';
import 'katex/dist/katex.min.css';

const QuizResultsPage = ({ 
  userAnswers, 
  questions, 
  selectedSet, 
  onBackToQuiz, 
  onTakeNewQuiz 
}) => {
  const [expandedQuestions, setExpandedQuestions] = useState(new Set());
  
  // Helper function to determine if a question has sub-questions
  const hasSubQuestions = useCallback((question) => {
    return question && typeof question.question_text === 'object' && 
           question.question_text.sub_questions && 
           Array.isArray(question.question_text.sub_questions);
  }, []);
  
  // Helper function to get sub-questions for a question
  const getSubQuestions = useCallback((question) => {
    if (hasSubQuestions(question)) {
      return question.question_text.sub_questions;
    }
    return [];
  }, [hasSubQuestions]);
  
  // Helper function to get question text (main part)
  const getMainQuestionText = useCallback((question) => {
    if (!question) return '';
    if (typeof question.question_text === 'string') {
      return question.question_text;
    }
    return question.question_text.main || '';
  }, []);
  
  // Normalize answers for comparison
  const normalizeAnswer = (answer) => {
    if (!answer) return '';
    return answer.toString()
      .replace(/\s+/g, '')
      .replace(/\$+/g, '')
      .toLowerCase()
      .trim();
  };
  
  // Check if answer is correct
  const isAnswerCorrect = useCallback((userAnswer, correctAnswer) => {
    const normalizedUser = normalizeAnswer(userAnswer);
    const normalizedCorrect = normalizeAnswer(correctAnswer);
    
    if (normalizedUser === normalizedCorrect) return true;
    
    // Additional checks for mathematical equivalence
    // Remove common latex commands for comparison
    const cleanUser = normalizedUser
      .replace(/\\frac\{([^}]+)\}\{([^}]+)\}/g, '($1)/($2)')
      .replace(/\\sqrt\{([^}]+)\}/g, 'sqrt($1)')
      .replace(/\\pi/g, 'pi')
      .replace(/\\infty/g, 'infinity');
      
    const cleanCorrect = normalizedCorrect
      .replace(/\\frac\{([^}]+)\}\{([^}]+)\}/g, '($1)/($2)')
      .replace(/\\sqrt\{([^}]+)\}/g, 'sqrt($1)')
      .replace(/\\pi/g, 'pi')
      .replace(/\\infty/g, 'infinity');
    
    return cleanUser === cleanCorrect;
  }, []);
  
  // Render math content properly
  const renderMathContent = (content) => {
    if (!content) return null;
    
    const contentString = content.toString();
    
    // Check if content contains LaTeX commands (mathematical notation) but no $ delimiters
    const mathCommands = /\\(int|frac|sqrt|pi|infty|sin|cos|tan|log|ln|sum|prod|lim|partial|nabla|alpha|beta|gamma|delta|epsilon|theta|lambda|mu|nu|rho|sigma|tau|phi|chi|psi|omega)/;
    const hasMathCommands = mathCommands.test(contentString);
    
    // If it contains math commands but no $ signs, wrap it in $ for LaTeX rendering
    if (hasMathCommands && !contentString.includes('$')) {
      try {
        return <InlineMath math={contentString} />;
      } catch (error) {
        console.warn('KaTeX rendering error for auto-wrapped content:', error);
        return <span>{contentString}</span>;
      }
    }
    
    // Check if this is pure math (wrapped in single $ pair) or mixed content
    if (contentString.startsWith('$') && contentString.endsWith('$') && contentString.indexOf('$', 1) === contentString.length - 1) {
      // Pure math content - just one $ at start and end
      const mathContent = contentString.slice(1, -1);
      try {
        return <InlineMath math={mathContent} />;
      } catch (error) {
        console.warn('KaTeX rendering error:', error);
        return <span>{contentString}</span>;
      }
    } else if (contentString.includes('$')) {
      // Mixed content - use regex to find all math expressions
      const parts = [];
      let lastIndex = 0;
      const mathRegex = /\$([^$]+)\$/g;
      let match;
      
      while ((match = mathRegex.exec(contentString)) !== null) {
        // Add text before the math expression
        if (match.index > lastIndex) {
          const textPart = contentString.slice(lastIndex, match.index);
          if (textPart) {
            parts.push({
              type: 'text',
              content: textPart,
              key: `text_${parts.length}`
            });
          }
        }
        
        // Add the math expression
        parts.push({
          type: 'math',
          content: match[1], // The content inside the $ signs
          key: `math_${parts.length}`
        });
        
        lastIndex = match.index + match[0].length;
      }
      
      // Add any remaining text after the last math expression
      if (lastIndex < contentString.length) {
        const textPart = contentString.slice(lastIndex);
        if (textPart) {
          parts.push({
            type: 'text',
            content: textPart,
            key: `text_${parts.length}`
          });
        }
      }
      
      return (
        <span>
          {parts.map((part) => {
            if (part.type === 'math') {
              try {
                return <InlineMath key={part.key} math={part.content} />;
              } catch (error) {
                console.warn('KaTeX rendering error for math part:', part.content, error);
                return <span key={part.key}>${part.content}$</span>;
              }
            } else {
              return <span key={part.key}>{part.content}</span>;
            }
          })}
        </span>
      );
    } else {
      // No $ signs - treat as plain text
      return <span>{contentString}</span>;
    }
  };

  // Calculate detailed results for each question
  const questionResults = useMemo(() => {
    if (!questions || questions.length === 0) {
      console.warn('No questions provided to QuizResultsPage');
      return [];
    }
    
    return questions.map((question, index) => {
      const userQuestionAnswers = userAnswers[index] || {};
      
      if (hasSubQuestions(question)) {
        const subQuestions = getSubQuestions(question);
        const subResults = subQuestions.map((subQ, subIndex) => {
          const partKey = `part_${subIndex + 1}`;
          const userAnswer = userQuestionAnswers.subAnswers?.[partKey] || '';
          const correctAnswer = question.final_answer?.[partKey] || '';
          

          
          return {
            partNumber: subQ.part_number,
            text: subQ.text,
            userAnswer,
            correctAnswer,
            isCorrect: isAnswerCorrect(userAnswer, correctAnswer),
            steps: question.solution_steps?.find(step => 
              step.part_number === subQ.part_number
            )?.steps || []
          };
        });
        
        const correctCount = subResults.filter(r => r.isCorrect).length;
        
        return {
          questionNumber: question.question_number,
          questionText: getMainQuestionText(question),
          hasSubQuestions: true,
          subResults,
          correctCount,
          totalCount: subResults.length,
          isFullyCorrect: correctCount === subResults.length,
          userAttempted: Object.keys(userQuestionAnswers.subAnswers || {}).length > 0
        };
      } else {
        const userAnswer = userQuestionAnswers.answer || '';
        const correctAnswer = question.final_answer || '';
        

        
        const isCorrect = isAnswerCorrect(userAnswer, correctAnswer);
        
        return {
          questionNumber: question.question_number,
          questionText: getMainQuestionText(question),
          hasSubQuestions: false,
          userAnswer,
          correctAnswer,
          isCorrect,
          steps: question.solution_steps || [],
          userAttempted: !!userAnswer.trim()
        };
      }
    });
  }, [questions, userAnswers, hasSubQuestions, getSubQuestions, getMainQuestionText, isAnswerCorrect]);
  
  // Calculate overall statistics
  const overallStats = useMemo(() => {
    let totalCorrect = 0;
    let totalQuestions = 0;
    let totalAttempted = 0;
    
    questionResults.forEach(result => {
      if (result.hasSubQuestions) {
        totalCorrect += result.correctCount;
        totalQuestions += result.totalCount;
        if (result.userAttempted) totalAttempted++;
      } else {
        totalQuestions++;
        if (result.isCorrect) totalCorrect++;
        if (result.userAttempted) totalAttempted++;
      }
    });
    
    return {
      totalCorrect,
      totalQuestions,
      totalAttempted,
      percentage: totalQuestions > 0 ? (totalCorrect / totalQuestions) * 100 : 0
    };
  }, [questionResults]);
  
  // Toggle question expansion
  const toggleQuestion = (index) => {
    const newExpanded = new Set(expandedQuestions);
    if (newExpanded.has(index)) {
      newExpanded.delete(index);
    } else {
      newExpanded.add(index);
    }
    setExpandedQuestions(newExpanded);
  };
  
  // Expand all questions
  const expandAll = () => {
    setExpandedQuestions(new Set(questionResults.map((_, index) => index)));
  };
  
  // Collapse all questions
  const collapseAll = () => {
    setExpandedQuestions(new Set());
  };
  
  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 via-indigo-50 to-purple-50">

      
      {/* Header */}
      <div className="bg-white shadow-sm border-b border-gray-200">
        <div className="max-w-6xl mx-auto px-4 py-6">
          <div className="flex justify-between items-center">
            <div>
              <h1 className="text-3xl font-bold text-gray-800">Quiz Results</h1>
              <p className="text-gray-600">{selectedSet} - Detailed Review</p>
            </div>
            <div className="flex space-x-3">
              <button
                onClick={onBackToQuiz}
                className="bg-gray-600 hover:bg-gray-700 text-white font-medium py-2 px-4 rounded-lg transition-colors"
              >
                Back to Quiz
              </button>
              <button
                onClick={onTakeNewQuiz}
                className="bg-blue-600 hover:bg-blue-700 text-white font-medium py-2 px-4 rounded-lg transition-colors"
              >
                Take New Quiz
              </button>
            </div>
          </div>
        </div>
      </div>
      
      {/* Overall Statistics */}
      <div className="max-w-6xl mx-auto px-4 py-6">
        <div className="bg-white rounded-lg shadow-lg p-6 mb-6">
          <h2 className="text-2xl font-bold text-gray-800 mb-4">Overall Performance</h2>
          
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
            <div className="text-center p-4 bg-green-50 rounded-lg">
              <div className="text-2xl font-bold text-green-600">{overallStats.totalCorrect}</div>
              <div className="text-sm text-gray-600">Correct Answers</div>
            </div>
            <div className="text-center p-4 bg-blue-50 rounded-lg">
              <div className="text-2xl font-bold text-blue-600">{overallStats.totalQuestions}</div>
              <div className="text-sm text-gray-600">Total Questions</div>
            </div>
            <div className="text-center p-4 bg-purple-50 rounded-lg">
              <div className="text-2xl font-bold text-purple-600">{overallStats.totalAttempted}</div>
              <div className="text-sm text-gray-600">Attempted</div>
            </div>
            <div className="text-center p-4 bg-yellow-50 rounded-lg">
              <div className="text-2xl font-bold text-yellow-600">{overallStats.percentage.toFixed(1)}%</div>
              <div className="text-sm text-gray-600">Score</div>
            </div>
          </div>
          
          {/* Progress bar */}
          <div className="w-full bg-gray-200 rounded-full h-3">
            <div 
              className="bg-green-600 h-3 rounded-full transition-all duration-300"
              style={{ width: `${overallStats.percentage}%` }}
            ></div>
          </div>
        </div>
        
        {/* Controls */}
        <div className="bg-white rounded-lg shadow-lg p-4 mb-6">
          <div className="flex justify-between items-center">
            <h3 className="text-lg font-semibold text-gray-800">Question Details</h3>
            <div className="flex space-x-3">
              <button
                onClick={expandAll}
                className="text-blue-600 hover:text-blue-700 font-medium text-sm"
              >
                Expand All
              </button>
              <button
                onClick={collapseAll}
                className="text-gray-600 hover:text-gray-700 font-medium text-sm"
              >
                Collapse All
              </button>
            </div>
          </div>
        </div>
        
        {/* Question Results */}
        <div className="space-y-4">
          {questionResults.map((result, index) => (
            <div key={index} className="bg-white rounded-lg shadow-lg overflow-hidden">
              {/* Question Header */}
              <div 
                className={`p-4 cursor-pointer transition-colors ${
                  result.hasSubQuestions 
                    ? (result.isFullyCorrect ? 'bg-green-50 hover:bg-green-100' : 'bg-red-50 hover:bg-red-100')
                    : (result.isCorrect ? 'bg-green-50 hover:bg-green-100' : 'bg-red-50 hover:bg-red-100')
                }`}
                onClick={() => toggleQuestion(index)}
              >
                <div className="flex justify-between items-start">
                  <div className="flex-1">
                    <div className="flex items-center space-x-3 mb-2">
                      <span className="bg-blue-100 text-blue-800 text-sm font-medium px-3 py-1 rounded-full">
                        {result.questionNumber}
                      </span>
                      <div className="flex items-center space-x-2">
                        {result.hasSubQuestions ? (
                          <span className={`text-sm font-medium px-2 py-1 rounded ${
                            result.isFullyCorrect 
                              ? 'bg-green-100 text-green-800' 
                              : result.correctCount > 0 
                                ? 'bg-yellow-100 text-yellow-800'
                                : 'bg-red-100 text-red-800'
                          }`}>
                            {result.correctCount}/{result.totalCount} correct
                          </span>
                        ) : (
                          <span className={`text-sm font-medium px-2 py-1 rounded ${
                            result.isCorrect 
                              ? 'bg-green-100 text-green-800' 
                              : 'bg-red-100 text-red-800'
                          }`}>
                            {result.isCorrect ? 'Correct' : 'Incorrect'}
                          </span>
                        )}
                        {!result.userAttempted && (
                          <span className="text-sm font-medium px-2 py-1 rounded bg-gray-100 text-gray-600">
                            Not attempted
                          </span>
                        )}
                      </div>
                    </div>
                    <div className="text-gray-800">
                      <ExplanationRenderer content={result.questionText} />
                    </div>
                  </div>
                  <div className="ml-4">
                    <svg 
                      className={`w-5 h-5 text-gray-500 transition-transform ${
                        expandedQuestions.has(index) ? 'rotate-180' : ''
                      }`}
                      fill="none" 
                      stroke="currentColor" 
                      viewBox="0 0 24 24"
                    >
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                    </svg>
                  </div>
                </div>
              </div>
              
              {/* Expanded Content */}
              {expandedQuestions.has(index) && (
                <div className="border-t border-gray-200">
                  {result.hasSubQuestions ? (
                    // Sub-questions view
                    <div className="p-6 space-y-6">
                      {result.subResults.map((subResult, subIndex) => (
                        <div key={subIndex} className="border border-gray-200 rounded-lg p-4">
                          <div className="mb-4">
                            <div className="flex items-center space-x-2 mb-2">
                              <span className="bg-blue-600 text-white text-sm font-medium px-2 py-1 rounded">
                                {subResult.partNumber}
                              </span>
                              <span className={`text-sm font-medium px-2 py-1 rounded ${
                                subResult.isCorrect 
                                  ? 'bg-green-100 text-green-800' 
                                  : 'bg-red-100 text-red-800'
                              }`}>
                                {subResult.isCorrect ? 'Correct' : 'Incorrect'}
                              </span>
                            </div>
                            <div className="text-gray-800 mb-3">
                              <ExplanationRenderer content={subResult.text} />
                            </div>
                          </div>
                          
                          {/* Answer comparison */}
                          <div className="grid md:grid-cols-2 gap-4 mb-4">
                            <div className="space-y-2">
                              <h4 className="font-medium text-gray-700">Your Answer:</h4>
                              <div className="p-3 bg-gray-50 rounded border">
                                {subResult.userAnswer ? (
                                  renderMathContent(subResult.userAnswer)
                                ) : (
                                  <span className="text-gray-500 italic">No answer provided</span>
                                )}
                              </div>
                            </div>
                            <div className="space-y-2">
                              <h4 className="font-medium text-gray-700">Correct Answer:</h4>
                              <div className="p-3 bg-green-50 rounded border border-green-200">
                                {subResult.correctAnswer ? (
                                  renderMathContent(subResult.correctAnswer)
                                ) : (
                                  <span className="text-red-500 italic">No correct answer found</span>
                                )}
                              </div>
                            </div>
                          </div>
                          
                          {/* Solution steps */}
                          {subResult.steps.length > 0 && (
                            <div className="space-y-2">
                              <h4 className="font-medium text-gray-700">Solution Steps:</h4>
                              <div className="bg-blue-50 rounded-lg p-4">
                                <ol className="space-y-2">
                                  {subResult.steps.map((step, stepIndex) => (
                                    <li key={stepIndex} className="flex">
                                      <span className="bg-blue-100 text-blue-800 text-xs font-medium px-2 py-1 rounded mr-3 mt-0.5 min-w-max">
                                        Step {stepIndex + 1}
                                      </span>
                                      <div className="flex-1">
                                        <ExplanationRenderer content={step} />
                                      </div>
                                    </li>
                                  ))}
                                </ol>
                              </div>
                            </div>
                          )}
                        </div>
                      ))}
                    </div>
                  ) : (
                    // Single question view
                    <div className="p-6">
                      {/* Answer comparison */}
                      <div className="grid md:grid-cols-2 gap-4 mb-6">
                        <div className="space-y-2">
                          <h4 className="font-medium text-gray-700">Your Answer:</h4>
                          <div className="p-3 bg-gray-50 rounded border">
                            {result.userAnswer ? (
                              renderMathContent(result.userAnswer)
                            ) : (
                              <span className="text-gray-500 italic">No answer provided</span>
                            )}
                          </div>
                        </div>
                        <div className="space-y-2">
                          <h4 className="font-medium text-gray-700">Correct Answer:</h4>
                          <div className="p-3 bg-green-50 rounded border border-green-200">
                            {result.correctAnswer ? (
                              renderMathContent(result.correctAnswer)
                            ) : (
                              <span className="text-red-500 italic">No correct answer found</span>
                            )}
                          </div>
                        </div>
                      </div>
                      
                      {/* Solution steps */}
                      {result.steps.length > 0 && (
                        <div className="space-y-2">
                          <h4 className="font-medium text-gray-700">Solution Steps:</h4>
                          <div className="bg-blue-50 rounded-lg p-4">
                            <ol className="space-y-3">
                              {result.steps.map((step, stepIndex) => (
                                <li key={stepIndex} className="flex">
                                  <span className="bg-blue-100 text-blue-800 text-xs font-medium px-2 py-1 rounded mr-3 mt-0.5 min-w-max">
                                    Step {stepIndex + 1}
                                  </span>
                                  <div className="flex-1">
                                    <ExplanationRenderer content={step} />
                                  </div>
                                </li>
                              ))}
                            </ol>
                          </div>
                        </div>
                      )}
                    </div>
                  )}
                </div>
              )}
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

export default QuizResultsPage;